import { z } from "zod";

/**
 * Townsy Casino protocol and rules (ADR-0016): blackjack at 3 tables of 5 seats, 8 slot machines, the money wheel and
 * a bar. Everything is played with PLAY CHIPS (just for fun, no value; one stack shared with Townsy Downs and kept for
 * the next visit, at least 100 on entry, ADR-0024). The server decides every card, reel and wheel stop; the browser
 * sends these intents in `venue.action` and only displays the state.
 */

// ---------------------------------------------------------------------------------------------------------------------
// Rules and limits (the server enforces them; the overlay shows them)
// ---------------------------------------------------------------------------------------------------------------------

export const CASINO_RULES = {
  blackjack: {
    tables: 3,
    seatsPerTable: 5,
    minBet: 1,
    maxBet: 50,
    decks: 6,
    /** The shoe is reshuffled before a round once this share of it has been dealt. */
    reshuffleAt: 0.75,
    /** The betting window opens with the first bet at an idle table (it closes early once every seated player bet). */
    bettingMs: 15_000,
    /** Each player's turn; the hand stands by itself when it runs out. */
    turnMs: 20_000,
    /** The dealer turns the hole card, then draws one card per step. */
    dealerStepMs: 800,
    resultMs: 5_000,
    /** Sit within this many metres of the table; walking further than `leaveRange` away frees the seat. */
    sitRange: 2.5,
    leaveRange: 5,
  },
  slots: {
    machines: 8,
    bets: [1, 5, 10] as const,
    spinMs: 2_000,
    /** Play within this many metres of the machine's play spot; walking further than `leaveRange` frees the machine. */
    range: 1.5,
    leaveRange: 3,
  },
  wheel: {
    bettingMs: 20_000,
    spinMs: 8_000,
    resultMs: 5_000,
    /** Bet within this many metres of the betting counter. */
    range: 6,
    minBet: 1,
    /** Most play chips one visitor can have on one round of the wheel (all symbols together). */
    maxPerRound: 50,
    /** Most separate bets one visitor can place on one round. */
    maxBetsPerRound: 12,
  },
  bar: {
    /** One free mocktail per visitor every this many milliseconds. */
    drinkCooldownMs: 20_000,
    /** How long the drink stays in the avatar's hand. */
    drinkShownMs: 45_000,
  },
} as const;

export const BLACKJACK_RULES_TEXT =
  "Dealer stands on all 17s and checks for blackjack. Blackjack pays 3 to 2 (rounded down to whole chips), a win pays 1 to 1, a tie returns your bet. Double on your first two cards. No split, no insurance.";

// ---------------------------------------------------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------------------------------------------------

export const CASINO_CARD_RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "T", "J", "Q", "K"] as const;
export const CASINO_CARD_SUITS = ["S", "H", "D", "C"] as const;
/** A card is rank + suit, e.g. "AS" (ace of spades), "TH" (ten of hearts). The dealer's hidden hole card is "XX". */
export type CasinoCard = string;
export const CASINO_HIDDEN_CARD = "XX";

export function casinoCardValue(card: CasinoCard): number {
  const r = card[0];
  if (r === "A") return 1;
  if (r === "T" || r === "J" || r === "Q" || r === "K") return 10;
  const n = Number(r);
  return Number.isInteger(n) && n >= 2 && n <= 9 ? n : 0;
}

/** Best blackjack total of a hand (aces count 11 when that does not bust); `soft` when an ace counts 11. */
export function blackjackTotal(cards: readonly CasinoCard[]): { total: number; soft: boolean } {
  let total = 0;
  let aces = 0;
  for (const c of cards) {
    if (c === CASINO_HIDDEN_CARD) continue;
    const v = casinoCardValue(c);
    total += v;
    if (v === 1) aces += 1;
  }
  if (aces > 0 && total + 10 <= 21) return { total: total + 10, soft: true };
  return { total, soft: false };
}

/** Two cards worth 21. */
export function isBlackjack(cards: readonly CasinoCard[]): boolean {
  return cards.length === 2 && blackjackTotal(cards).total === 21;
}

/** Chips returned for a blackjack: the stake plus 3 to 2, rounded DOWN to whole chips (a 5-chip bet returns 12). */
export function blackjackPayout(stake: number): number {
  return stake + Math.floor((stake * 3) / 2);
}

// ---------------------------------------------------------------------------------------------------------------------
// Slots: three reels of 20 stops (7 x2, BAR x2, bell x4, cherry x5, blank x7 on each reel)
// ---------------------------------------------------------------------------------------------------------------------

export const SlotSymbol = z.enum(["seven", "bar", "bell", "cherry", "blank"]);
export type SlotSymbol = z.infer<typeof SlotSymbol>;

export const SLOT_SYMBOL_LABELS: Record<SlotSymbol, string> = { seven: "7", bar: "BAR", bell: "Bell", cherry: "Cherry", blank: "Blank" };

const S7 = "seven";
const BR = "bar";
const BE = "bell";
const CH = "cherry";
const __ = "blank";
/** The reel strips (top to bottom). The same symbol counts on every reel, in a different order. */
export const SLOT_REELS: readonly (readonly SlotSymbol[])[] = [
  [S7, __, CH, BE, __, BR, CH, __, BE, CH, S7, __, BE, CH, BR, __, BE, CH, __, __],
  [CH, S7, __, BE, CH, __, BR, __, CH, BE, __, S7, CH, __, BE, BR, __, CH, BE, __],
  [BE, __, CH, S7, __, CH, BR, __, BE, __, CH, BE, __, S7, CH, __, BR, BE, CH, __],
];
export const SLOT_STOPS = 20;

/** What a spin pays, as a multiple of the bet (the bet itself is taken when the reels start). First match wins. */
export const SLOT_PAYTABLE: ReadonlyArray<{ label: string; pays: number; three?: SlotSymbol; cherries?: number }> = [
  { label: "7 7 7", pays: 50, three: "seven" },
  { label: "BAR BAR BAR", pays: 20, three: "bar" },
  { label: "Bell Bell Bell", pays: 10, three: "bell" },
  { label: "Cherry Cherry Cherry", pays: 5, three: "cherry" },
  { label: "Any two cherries", pays: 2, cherries: 2 },
  { label: "Any one cherry", pays: 1, cherries: 1 },
];

/** Multiplier for three symbols on the payline (0 = no win). */
export function slotMultiplier(symbols: readonly SlotSymbol[]): number {
  const cherries = symbols.filter((s) => s === "cherry").length;
  for (const line of SLOT_PAYTABLE) {
    if (line.three && symbols.length === 3 && symbols.every((s) => s === line.three)) return line.pays;
    if (line.cherries !== undefined && cherries === line.cherries) return line.pays;
  }
  return 0;
}

/** The payline symbols for three reel stops. */
export function slotSymbolsAt(stops: readonly number[]): SlotSymbol[] {
  return SLOT_REELS.map((reel, i) => reel[(((stops[i] ?? 0) % SLOT_STOPS) + SLOT_STOPS) % SLOT_STOPS]!);
}

// ---------------------------------------------------------------------------------------------------------------------
// Money wheel: 54 segments (1 x24, 2 x15, 5 x7, 10 x4, 20 x2, star x2)
// ---------------------------------------------------------------------------------------------------------------------

export const MoneyWheelSymbol = z.enum(["1", "2", "5", "10", "20", "star"]);
export type MoneyWheelSymbol = z.infer<typeof MoneyWheelSymbol>;
export const MONEY_WHEEL_SYMBOLS: readonly MoneyWheelSymbol[] = MoneyWheelSymbol.options;
export const MONEY_WHEEL_SEGMENT_COUNTS: Record<MoneyWheelSymbol, number> = { "1": 24, "2": 15, "5": 7, "10": 4, "20": 2, star: 2 };
/** "N to 1": a winning bet gets its stake back plus stake x N. */
/** The star pays 24 to 1 (not 40: with two star segments 40 to 1 would return 152% of every star bet; 24 to 1 returns 92.6%). */
export const MONEY_WHEEL_PAYS: Record<MoneyWheelSymbol, number> = { "1": 1, "2": 2, "5": 5, "10": 10, "20": 20, star: 24 };
export const MONEY_WHEEL_LABELS: Record<MoneyWheelSymbol, string> = { "1": "1", "2": "2", "5": "5", "10": "10", "20": "20", star: "Star" };

/**
 * The segments clockwise from the top (as seen from the front), spread evenly: each next segment is the symbol furthest
 * behind its share so far (ties: the rarer symbol). Deterministic, so the server and every client agree.
 */
export const MONEY_WHEEL_SEGMENTS: readonly MoneyWheelSymbol[] = (() => {
  const total = Object.values(MONEY_WHEEL_SEGMENT_COUNTS).reduce((a, b) => a + b, 0);
  const placed: Record<MoneyWheelSymbol, number> = { "1": 0, "2": 0, "5": 0, "10": 0, "20": 0, star: 0 };
  const out: MoneyWheelSymbol[] = [];
  const order: MoneyWheelSymbol[] = ["star", "20", "10", "5", "2", "1"];
  for (let i = 0; i < total; i++) {
    let best: MoneyWheelSymbol = "1";
    let bestDeficit = -Infinity;
    for (const s of order) {
      if (placed[s] >= MONEY_WHEEL_SEGMENT_COUNTS[s]) continue;
      const deficit = ((i + 0.5) * MONEY_WHEEL_SEGMENT_COUNTS[s]) / total - placed[s];
      if (deficit > bestDeficit + 1e-9) {
        bestDeficit = deficit;
        best = s;
      }
    }
    placed[best] += 1;
    out.push(best);
  }
  return out;
})();

/** Chips returned for a winning wheel bet (stake back plus the odds); 0 for a losing one. */
export function moneyWheelReturn(bet: MoneyWheelSymbol, result: MoneyWheelSymbol, stake: number): number {
  return bet === result ? stake * (MONEY_WHEEL_PAYS[bet] + 1) : 0;
}

// ---------------------------------------------------------------------------------------------------------------------
// Bar: free mocktails (no chips, no alcohol), just a social touch
// ---------------------------------------------------------------------------------------------------------------------

export const CasinoDrink = z.enum(["sunset_fizz", "mint_cooler", "berry_spritz", "lemon_sparkle"]);
export type CasinoDrink = z.infer<typeof CasinoDrink>;
export const CASINO_DRINKS: Record<CasinoDrink, { name: string; color: string }> = {
  sunset_fizz: { name: "Sunset Fizz", color: "#F28A3C" },
  mint_cooler: { name: "Mint Cooler", color: "#5CC79A" },
  berry_spritz: { name: "Berry Spritz", color: "#C24A86" },
  lemon_sparkle: { name: "Lemon Sparkle", color: "#F2D04A" },
};

/** The bartender's name tag (the AI badge comes from its AI resident id). */
export const CASINO_BARTENDER_LABEL = "Bartender Sam";

// ---------------------------------------------------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------------------------------------------------

const Chips = z.number().int().min(1).max(CASINO_RULES.blackjack.maxBet);

/** Client -> server (`venue.action`'s `action` for the casino). */
export const CasinoAction = z.discriminatedUnion("type", [
  /** Sit at a table (a free seat, or the given one). */
  z.object({ type: z.literal("bj.sit"), table: z.number().int().min(0).max(2), seat: z.number().int().min(0).max(4).optional() }).strict(),
  /** Leave your seat (a bet already placed comes back before the deal; after the deal it stays on the table and is lost). */
  z.object({ type: z.literal("bj.leave") }).strict(),
  /** Deal me in for the next round with this many chips. */
  z.object({ type: z.literal("bj.bet"), amount: Chips }).strict(),
  z.object({ type: z.literal("bj.hit") }).strict(),
  z.object({ type: z.literal("bj.stand") }).strict(),
  z.object({ type: z.literal("bj.double") }).strict(),
  z.object({ type: z.literal("slot.spin"), machine: z.number().int().min(0).max(7), bet: z.union([z.literal(1), z.literal(5), z.literal(10)]) }).strict(),
  z.object({ type: z.literal("wheel.bet"), symbol: MoneyWheelSymbol, amount: Chips }).strict(),
  /** Take back all of your bets on the current wheel round (betting phase only; the chips come back exactly). */
  z.object({ type: z.literal("wheel.clear") }).strict(),
  z.object({ type: z.literal("bar.drink"), drink: CasinoDrink }).strict(),
]);
export type CasinoAction = z.infer<typeof CasinoAction>;

export type BlackjackPhase = "idle" | "betting" | "playing" | "dealer" | "result";
export type BlackjackOutcome = "blackjack" | "win" | "push" | "lose" | "bust";

export interface BlackjackSeatState {
  actorId: string;
  label: string;
  /** Chips on the table for this round (doubled after a double); 0 = not dealt in. */
  bet: number;
  cards: CasinoCard[];
  total: number;
  soft: boolean;
  doubled: boolean;
  /** Finished acting (stood, doubled, bust, 21 or blackjack). */
  done: boolean;
  outcome: BlackjackOutcome | null;
  /** Chips returned at the end of the round (0 for a loss). */
  payout: number;
}

export interface BlackjackTableState {
  phase: BlackjackPhase;
  /** Milliseconds left in the betting window, the current turn or the result display (null when nothing is timed). */
  endsIn: number | null;
  /** Seat whose turn it is (playing phase). */
  turn: number | null;
  /** Dealer cards; the hole card is "XX" until the dealer plays. */
  dealer: CasinoCard[];
  /** Total of the dealer's visible cards. */
  dealerTotal: number;
  seats: Array<BlackjackSeatState | null>;
  round: number;
  /** Cards left in the shoe. */
  shoe: number;
}

export interface SlotMachineState {
  /** Who plays this machine now (null = free). */
  actorId: string | null;
  label: string | null;
  spinning: boolean;
  /** Increases with every spin (the renderer animates when it changes). */
  spin: number;
  /** Reel stops of the last spin (decided when the reels start; the renderer animates to them). */
  stops: [number, number, number];
  bet: number;
  /** Chips the last spin paid (known once the reels stopped; 0 while spinning). */
  win: number;
  /** Milliseconds until the reels stop (spinning only). */
  endsIn: number | null;
}

export type MoneyWheelPhase = "betting" | "spinning" | "result";

export interface MoneyWheelState {
  phase: MoneyWheelPhase;
  endsIn: number;
  round: number;
  /** The segment under the pointer: the winning one, chosen when the wheel starts (while betting: the last result; null before the first spin). */
  segment: number | null;
  result: MoneyWheelSymbol | null;
  /** Latest results, newest first (at most 12). */
  history: MoneyWheelSymbol[];
  /** Play chips on each symbol this round (everyone together). */
  pot: Record<MoneyWheelSymbol, number>;
  /** Number of people with a bet this round. */
  players: number;
}

export interface CasinoDrinkState {
  actorId: string;
  drink: CasinoDrink;
  /** Milliseconds the drink stays shown. */
  endsIn: number;
}

/** The AI bartender behind the bar (an AI resident: AI badge, mute and report like the others). */
export interface CasinoBartenderState {
  actorId: string;
  label: string;
}

/** Server -> everyone (`venue.state` for the casino). */
export interface CasinoState {
  tables: BlackjackTableState[];
  slots: SlotMachineState[];
  wheel: MoneyWheelState;
  drinks: CasinoDrinkState[];
  /** Null when AI residents are off on this server (the bar is self-service then). */
  bartender: CasinoBartenderState | null;
  /** Betting with play chips is on (VENUE_CHANCE_GAMES_ENABLED). */
  bettingOpen: boolean;
}

export type CasinoNear =
  | { kind: "table"; table: number }
  | { kind: "slot"; machine: number }
  | { kind: "wheel" }
  | { kind: "bar" };

/** Server -> one visitor (`venue.private` for the casino). */
export interface CasinoPrivate {
  /** What the visitor stands at (the overlay shows those controls). */
  near: CasinoNear | null;
  seat: { table: number; seat: number } | null;
  /** The slot machine the visitor is playing (held while they stay close). */
  machine: number | null;
  /** The visitor's bets on the current wheel round. */
  wheelBets: Partial<Record<MoneyWheelSymbol, number>>;
  wheelRound: number;
  /** When the visitor may order the next drink (milliseconds from now; 0 = now). */
  drinkIn: number;
}

/** `result` / `win` event data. */
export interface CasinoResultData {
  game: "blackjack" | "slots" | "wheel";
  /** Chips staked on what just finished. */
  staked: number;
  /** Chips returned (0 for a loss). */
  returned: number;
}
